import type { RosterFilterParams } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { UserFilterParams } from '@/types/api';
import type { CompanyManager } from '@/types/directory/Company';
import type { User } from '@/types/directory/User';
import type { CompanyAttributeMap } from '@/utils/company';

import { ATTRIBUTE_BUCKETS, ATTRIBUTE_FAMILIES_WITH_OWN_BUCKET } from '@/constants/Filters';
import { defaultFiltersState } from '@/hooks/filters/useCompanyAttributesFilters';

/** The picked ids of one attribute family, or `undefined` when nothing is picked in it. */
const selectedIds = (items: Array<{ id?: string }> | undefined): string[] | undefined =>
    items && items.length > 0
        ? items.map((item) => item.id).filter((id): id is string => Boolean(id))
        : undefined;

/**
 * Maps a `CompanyAttributeFilters` selection to the six id-array query params every list
 * endpoint filtering on company attributes takes, leaving an untouched family `undefined` so
 * axios omits the param entirely. `toRosterParams` below is the roster endpoint's own variant,
 * which bundles the same six with that endpoint's `searchQuery` and audience flags.
 */
export const toAttributeIdParams = (filters: SelectedFilters): UserFilterParams => ({
    departmentIds: selectedIds(filters.departments),
    jobTitleIds: selectedIds(filters.jobTitles),
    managerIds: selectedIds(filters.managers as CompanyManager[]),
    companyAddressIds: selectedIds(filters.companyAddresses),
    employeeLevelIds: selectedIds(filters.employeeLevels),
    customAttributeIds: selectedIds(filters.customAttributes),
});

/**
 * Maps a `CompanyAttributeFilters` selection to the roster query's id-array params. Promoted
 * from `CompetencyProfilesAdminPage` so every page resolving a company-attribute filter through
 * the roster endpoint (`/EmployeeCompetencyProfileAssignment/roster`) shares one mapping instead
 * of re-deriving it. Mirrors the `buildFilterParams` pattern from `EmployeeRuleSelector`.
 */
export const toRosterParams = (filters: SelectedFilters, searchQuery = ''): RosterFilterParams => ({
    departmentIds: selectedIds(filters.departments),
    jobTitleIds: selectedIds(filters.jobTitles),
    managerIds: selectedIds(filters.managers as CompanyManager[]),
    companyAddressIds: selectedIds(filters.companyAddresses),
    employeeLevelIds: selectedIds(filters.employeeLevels),
    customAttributeIds: selectedIds(filters.customAttributes),
    searchQuery: searchQuery.trim() || undefined,
    isActiveOnly: true,
    includeInvited: true,
});

/**
 * Whether `user` meets every family `filters` picks a value in, as the directory matches them: a
 * family is met when the user's value is one of those picked, and the custom attributes, pooled in
 * one bucket, when any of the user's custom values is.
 */
export const matchesSelectedFilters = (user: User, filters: SelectedFilters): boolean => {
    const userValuesByBucket: Record<string, Array<string | null>> = {
        departments: [user.departmentId],
        jobTitles: [user.jobTitleId],
        managers: [user.managerId],
        companyAddresses: [user.companyAddressId],
        employeeLevels: [user.employeeLevelId],
        customAttributes: user.customAttributes.map(({ value }) => value),
    };

    return Object.entries(userValuesByBucket).every(([bucket, userValues]) => {
        const pickedIds = selectedIds(filters[bucket]);

        return !pickedIds || userValues.some((value) => !!value && pickedIds.includes(value));
    });
};

/** Whether a `CompanyAttributeFilters` selection has at least one value picked, in any family. */
export const hasSelectedFilters = (filters: SelectedFilters | undefined): boolean =>
    !!filters && Object.values(filters).some((values) => values.length > 0);

/**
 * The attribute families a picker opens on when it is seeded with `filters`: the families the
 * selection fills. A selection keeps a named bucket for each base family and pours every other
 * family's values -- the custom attributes -- into `customAttributes`, so such a family opens when
 * one of its own values is there.
 */
export const familiesSeededBy = (
    attributes: CompanyAttributeMap,
    filters: SelectedFilters,
): CompanyAttributeMap => {
    const seededCustomIds = new Set((filters.customAttributes ?? []).map((value) => value.id));
    const holdsSeededValue = (family: string, attribute: CompanyAttributeMap[string]) =>
        !ATTRIBUTE_FAMILIES_WITH_OWN_BUCKET.includes(family) &&
        attribute.values.some((value) => seededCustomIds.has(value.id));

    return Object.fromEntries(
        Object.entries(attributes).filter(
            ([family, attribute]) =>
                (filters[family] ?? []).length > 0 || holdsSeededValue(family, attribute),
        ),
    );
};

/**
 * The selection with only the values `attributes` still offers, family by family: an id the
 * directory no longer knows (a value since deleted, an address typed by hand) would narrow the list
 * to nobody, with nothing on screen showing why. Custom values are kept when one of the custom
 * attributes offers them.
 */
export const keepOfferedValues = (
    filters: SelectedFilters,
    attributes: CompanyAttributeMap,
): SelectedFilters => {
    const customFamilies = Object.keys(attributes).filter(
        (family) => !ATTRIBUTE_FAMILIES_WITH_OWN_BUCKET.includes(family),
    );
    const offeredIdsIn = (bucket: string) =>
        new Set(
            (ATTRIBUTE_FAMILIES_WITH_OWN_BUCKET.includes(bucket)
                ? [bucket]
                : customFamilies
            ).flatMap((family) => (attributes[family]?.values ?? []).map((value) => value.id)),
        );
    // `filter` on a union of arrays widens the element type; a bucket only loses values, so it
    // keeps its own.
    const keepOffered = <V extends SelectedFilters[string]>(values: V, bucket: string): V => {
        const offered = offeredIdsIn(bucket);

        return values.filter((value) => offered.has(value.id)) as V;
    };

    return {
        ...defaultFiltersState,
        ...Object.fromEntries(
            ATTRIBUTE_BUCKETS.map((bucket) => [bucket, keepOffered(filters[bucket] ?? [], bucket)]),
        ),
    };
};

/** The ids a selection picks, family by family, as one comparable string. */
export const selectedIdsKey = (filters: SelectedFilters | undefined): string =>
    ATTRIBUTE_BUCKETS.map((family) => (selectedIds(filters?.[family]) ?? []).join(',')).join('|');
